import { AlertTriangle } from 'lucide-react';
import type {
    ReceivingExceptionsKpis,
    ReceiptStatusBreakdownRow,
    ExceptionTagBreakdownRow,
    DailyTrendRow,
    TopCarrierRow,
    TopClientRow,
    ExceptionDetailRow,
} from '../../types/analyticsReceiving';

export interface ReceivingExceptionsExportProps {
    facilityName: string;
    rangeLabel: string;
    startDate: Date;
    endDate: Date;
    computedAt: number;
    kpis: ReceivingExceptionsKpis;
    receiptStatusBreakdown: ReceiptStatusBreakdownRow[];
    exceptionTagBreakdown: ExceptionTagBreakdownRow[];
    dailyTrend: DailyTrendRow[];
    topCarriers: TopCarrierRow[];
    topClients: TopClientRow[];
    details: ExceptionDetailRow[];
    isTruncated: boolean;
    onClose: () => void;
}

function formatDate(d: Date): string {
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function ReceivingExceptionsExport({
    facilityName,
    rangeLabel,
    startDate,
    endDate,
    computedAt,
    kpis,
    receiptStatusBreakdown,
    exceptionTagBreakdown,
    dailyTrend,
    topCarriers,
    topClients,
    details,
    isTruncated,
    onClose,
}: ReceivingExceptionsExportProps) {
    const handlePrint = () => window.print();

    return (
        <div className="fixed inset-0 z-[400] flex items-center justify-center bol-print-wrapper">

            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm print:hidden"
                onClick={onClose}
            />

            {/* Document card */}
            <div className="relative w-full max-w-2xl max-h-[92vh] print:mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col print:max-h-none print:rounded-none print:shadow-none print:overflow-visible print:max-w-none">

                {/* Toolbar — screen only */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-slate-50 print:hidden">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                        <AlertTriangle size={15} className="text-orange-500" />
                        Receiving Exceptions Report
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            title="For clean PDF export: disable 'Headers and footers' in your browser print settings."
                            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors"
                        >
                            Print / Save PDF
                        </button>
                        <button
                            onClick={onClose}
                            className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-auto print:overflow-visible">
                    <div className="bol-print p-10 space-y-7 text-slate-900 print:text-black print:bg-white print:p-0 print:space-y-6">

                        {/* ── Section A: Document title ── */}
                        <div className="text-center border-b-2 border-slate-900 pb-5 print:border-black print-avoid-break">
                            <div className="flex items-center justify-center gap-2 mb-1">
                                <AlertTriangle size={18} className="text-orange-600 print:text-black" />
                                <h1 className="text-xl font-bold tracking-tight uppercase text-slate-900 print:text-black">
                                    Receiving Exceptions Report
                                </h1>
                            </div>
                            <p className="text-sm text-slate-600 print:text-slate-700 font-medium mt-1">
                                Generated by BayWhiz
                            </p>
                        </div>

                        {/* ── Section B: Facility details ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Facility Details</SectionLabel>
                            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 border border-slate-300 print:border-black rounded-none p-4">
                                <InfoField label="Facility" value={facilityName} />
                                <InfoField label="Reporting Period" value={`${formatDate(startDate)} – ${formatDate(endDate)}`} />
                                <InfoField label="Range" value={rangeLabel} />
                                <InfoField label="Generated" value={new Date(computedAt).toLocaleString()} />
                            </div>
                        </div>

                        {/* ── Section C: KPI summary ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Key Performance Indicators</SectionLabel>
                            <table className="w-full mt-3 text-sm border-collapse">
                                <thead>
                                    <tr className="bg-slate-100 print:bg-slate-100">
                                        <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Metric</th>
                                        <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Value</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                    <KpiRow label="Total POs Reviewed" value={String(kpis.totalPOsReviewed)} />
                                    <KpiRow label="Total Received" value={String(kpis.totalReceived)} />
                                    <KpiRow label="Total Exceptions" value={String(kpis.totalExceptions)} bold />
                                    <KpiRow label="Exception Rate" value={`${kpis.exceptionRatePct}%`} bold />
                                    <KpiRow label="Shortage Count" value={String(kpis.shortageCount)} />
                                    <KpiRow label="Overage Count" value={String(kpis.overageCount)} />
                                    <KpiRow label="Damaged Count" value={String(kpis.damagedCount)} />
                                    <KpiRow label="Refused Count" value={String(kpis.refusedCount)} />
                                    <KpiRow label="Pending Receipts" value={String(kpis.pendingReceiptCount)} />
                                    <KpiRow label="Unresolved Exceptions" value={String(kpis.unresolvedCount)} />
                                    <KpiRow label="Follow-ups Created" value={String(kpis.followupCreatedCount)} />
                                    <KpiRow label="Resolved" value={String(kpis.resolvedCount)} />
                                </tbody>
                            </table>
                        </div>

                        {/* ── Section D: Receipt Outcome Breakdown ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Receipt Outcome Breakdown</SectionLabel>
                            {receiptStatusBreakdown.length > 0 ? (
                                <table className="w-full mt-3 text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 print:bg-slate-100">
                                            <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Status</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Count</th>
                                            <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Percentage</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                        {receiptStatusBreakdown.map((row) => (
                                            <tr key={row.status}>
                                                <td className="px-4 py-2 text-slate-800 print:text-black border border-slate-200 print:border-black font-medium capitalize">{row.status}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{row.count}</td>
                                                <td className="px-4 py-2 text-right text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{row.pct}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="mt-3 text-sm text-slate-500 italic">No receiving exceptions recorded for this period.</p>
                            )}
                        </div>

                        {/* ── Section E: Exception Tag Breakdown ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Exception Tag Breakdown</SectionLabel>
                            {exceptionTagBreakdown.length > 0 ? (
                                <table className="w-full mt-3 text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 print:bg-slate-100">
                                            <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Exception Tag</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Count</th>
                                            <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Percentage</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                        {exceptionTagBreakdown.map((row) => (
                                            <tr key={row.type}>
                                                <td className="px-4 py-2 text-slate-800 print:text-black border border-slate-200 print:border-black font-medium">{row.label}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{row.count}</td>
                                                <td className="px-4 py-2 text-right text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{row.pct}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="mt-3 text-sm text-slate-500 italic">No exception tags recorded for this period.</p>
                            )}
                        </div>

                        {/* ── Daily Exception Trend ── */}
                        {dailyTrend.length > 0 && dailyTrend.some(t => t.totalPOs > 0) && (
                            <div className="print-avoid-break">
                                <SectionLabel>Daily Exception Trend</SectionLabel>
                                <table className="w-full mt-3 text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 print:bg-slate-100">
                                            <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Date</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Total POs</th>
                                            <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Exceptions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                        {dailyTrend.filter(t => t.totalPOs > 0).map((t) => (
                                            <tr key={t.date}>
                                                <td className="px-4 py-2 text-slate-700 print:text-black border border-slate-200 print:border-black">{t.date}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{t.totalPOs}</td>
                                                <td className="px-4 py-2 text-right border border-slate-200 print:border-black font-mono font-bold" style={{ color: t.exceptionCount > 0 ? '#ea580c' : '#64748b' }}>{t.exceptionCount}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Section F: Top Carriers ── */}
                        {topCarriers.length > 0 && (
                            <div className="print-avoid-break">
                                <SectionLabel>Top Carriers by Exceptions</SectionLabel>
                                <table className="w-full mt-3 text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 print:bg-slate-100">
                                            <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Carrier</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">POs</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Exceptions</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Rate</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Shortages</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Damages</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                        {topCarriers.map((c) => (
                                            <tr key={c.carrierName}>
                                                <td className="px-4 py-2 text-slate-800 print:text-black border border-slate-200 print:border-black font-medium">{c.carrierName}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{c.totalPOs}</td>
                                                <td className="px-4 py-2 text-center border border-slate-200 print:border-black font-mono font-bold" style={{ color: c.exceptionCount > 0 ? '#ea580c' : '#64748b' }}>{c.exceptionCount}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{c.exceptionRatePct}%</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{c.shortages}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{c.damages}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Section G: Top Clients ── */}
                        {topClients.length > 0 && (
                            <div className="print-avoid-break">
                                <SectionLabel>Top Clients / Vendors by Exceptions</SectionLabel>
                                <table className="w-full mt-3 text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 print:bg-slate-100">
                                            <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Client / Vendor</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">POs</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Exceptions</th>
                                            <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                        {topClients.map((c) => (
                                            <tr key={c.clientName}>
                                                <td className="px-4 py-2 text-slate-800 print:text-black border border-slate-200 print:border-black font-medium">{c.clientName}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{c.totalPOs}</td>
                                                <td className="px-4 py-2 text-center border border-slate-200 print:border-black font-mono font-bold" style={{ color: c.exceptionCount > 0 ? '#ea580c' : '#64748b' }}>{c.exceptionCount}</td>
                                                <td className="px-4 py-2 text-right text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{c.exceptionRatePct}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Section H: Exception Detail Table ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Exception Detail</SectionLabel>
                            {isTruncated && (
                                <p className="mt-2 text-xs text-amber-600 print:text-amber-800 font-semibold">
                                    Detail table limited to latest 500 rows.
                                </p>
                            )}
                            {details.length > 0 ? (
                                <table className="w-full mt-3 text-[11px] border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 print:bg-slate-100">
                                            {['Date', 'Carrier', 'Dock', 'PO', 'Client', 'Exp', 'Rcv', 'Var', 'Status', 'Exception', 'Resolution', 'Follow-up'].map((h) => (
                                                <th key={h} className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-2 py-1.5 border border-slate-300 print:border-black">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                        {details.map((row) => {
                                            const fmtDate = row.appointmentDate
                                                ? new Date(row.appointmentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                                : '-';
                                            const variance = row.variance !== null
                                                ? (row.variance > 0 ? `+${row.variance}` : String(row.variance))
                                                : '-';
                                            return (
                                                <tr key={row.appointmentPosId}>
                                                    <td className="px-2 py-1.5 text-slate-700 print:text-black border border-slate-200 print:border-black whitespace-nowrap">{fmtDate}</td>
                                                    <td className="px-2 py-1.5 text-slate-700 print:text-black border border-slate-200 print:border-black">{row.carrierName}</td>
                                                    <td className="px-2 py-1.5 text-slate-600 border border-slate-200 print:border-black">{row.dockNumber || '-'}</td>
                                                    <td className="px-2 py-1.5 text-slate-800 print:text-black border border-slate-200 print:border-black font-mono font-semibold">{row.poNumber}</td>
                                                    <td className="px-2 py-1.5 text-slate-700 print:text-black border border-slate-200 print:border-black">{row.clientName}</td>
                                                    <td className="px-2 py-1.5 text-center text-slate-600 border border-slate-200 print:border-black font-mono">{row.expectedQty ?? '-'}</td>
                                                    <td className="px-2 py-1.5 text-center text-slate-600 border border-slate-200 print:border-black font-mono">{row.receivedQty ?? '-'}</td>
                                                    <td className="px-2 py-1.5 text-center border border-slate-200 print:border-black font-mono font-bold" style={{ color: (row.variance ?? 0) < 0 ? '#dc2626' : (row.variance ?? 0) > 0 ? '#2563eb' : '#64748b' }}>{variance}</td>
                                                    <td className="px-2 py-1.5 border border-slate-200 print:border-black capitalize">{row.receiptStatus || '-'}</td>
                                                    <td className="px-2 py-1.5 border border-slate-200 print:border-black">{row.exceptionLabel || '-'}</td>
                                                    <td className="px-2 py-1.5 border border-slate-200 print:border-black capitalize">{row.resolutionStatus?.replace(/_/g, ' ') || '-'}</td>
                                                    <td className="px-2 py-1.5 border border-slate-200 print:border-black font-mono">{row.followupPoNumber || '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="mt-3 text-sm text-slate-500 italic">No receiving exceptions recorded for this period.</p>
                            )}
                        </div>

                        {/* ── Section I: Methodology ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Methodology</SectionLabel>
                            <p className="mt-3 text-sm text-slate-700 print:text-slate-800 leading-relaxed border border-slate-300 print:border-black p-4">
                                This report is based on BayWhiz receipt reconciliation records linked to appointment POs. Exceptions include shortages, overages, damaged freight, refused freight, and receiving exception tags recorded during warehouse receiving.
                            </p>
                        </div>

                        {/* ── Footer ── */}
                        <div className="mt-8 pt-4 border-t-2 border-slate-300 print:border-black text-center print-avoid-break">
                            <p className="text-[11px] text-slate-400 print:text-slate-500">
                                Generated by <strong>BayWhiz Dock Scheduling Platform</strong>
                            </p>
                            <p className="text-[11px] text-slate-400 print:text-slate-500">
                                Receiving exceptions derived from appointment receipt reconciliation records
                            </p>
                            <p className="text-[11px] text-slate-400 print:text-slate-500 mt-1">
                                Report computed at {new Date(computedAt).toLocaleString()}
                            </p>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Sub-components (same pattern as DwellAnalyticsExport) ── */
function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 print:text-slate-600 border-b border-slate-200 print:border-slate-400 pb-1">
            {children}
        </h2>
    );
}

function InfoField({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 print:text-slate-500 mb-0.5">{label}</div>
            <div className="text-sm font-semibold text-slate-900 print:text-black">{value}</div>
        </div>
    );
}

function KpiRow({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
    return (
        <tr>
            <td className={`px-4 py-2.5 text-slate-700 print:text-slate-800 border border-slate-200 print:border-black ${bold ? 'font-bold' : ''}`}>{label}</td>
            <td className={`px-4 py-2.5 text-right text-slate-900 print:text-black border border-slate-200 print:border-black font-mono ${bold ? 'font-bold' : ''}`}>{value}</td>
        </tr>
    );
}
